import { useCalls } from '@hostesa/api-client';
import { useMemo } from 'react';
import { bucketCallsByDay } from './call-buckets.ts';
import type { CallVolumeVM, PresentationResult } from './types.ts';

const VOLUME_WINDOW_DAYS = 14;

/**
 * 14-day call volume, computed from the canonical `useCalls` window (same query
 * key as the desk hook — no extra request). Calls are bucketed by calendar day
 * in the hotel timezone: inbound = all calls, bookings = completed, transferred
 * = handed to a human. Days with no data render as honest zeros; when there are
 * no calls at all the series is empty and the page hides the chart.
 *
 * NOTE: the 50-call window under-counts once daily volume climbs — acceptable
 * until a stats/aggregate endpoint exists. Do NOT silently raise the limit.
 */
export function useCallVolume(
  tenantId: string,
  hotelId: string,
  timeZone: string,
): PresentationResult<CallVolumeVM> {
  const calls = useCalls(tenantId, hotelId, { limit: 50 });
  const data = useMemo<CallVolumeVM | undefined>(() => {
    if (!calls.data) return undefined;
    const items = calls.data.items;
    if (items.length === 0) return { series: [], xLabels: [] };
    const b = bucketCallsByDay(items, timeZone, VOLUME_WINDOW_DAYS);
    return {
      xLabels: b.labels,
      series: [
        { id: 'inbound', color: 'var(--color-charcoal)', data: b.all, filled: true },
        { id: 'bookings', color: 'var(--color-warm-stone)', data: b.completed },
        { id: 'transferred', color: 'var(--color-stone-400)', data: b.transferred, dashed: true },
      ],
    };
  }, [calls.data, timeZone]);
  return { data, isLoading: calls.isLoading, isError: calls.isError };
}
